import React, { useState } from "react";
import { Card, Form, Input, Button, Alert, Descriptions, Tag, Modal, message } from "antd";
import axios from "axios";
import { formatDateTime } from "../../helpers/date";
import { formatAmount } from "../../helpers/billingStatement";
import { formatCardSummary } from "../../helpers/cardBinding";
import {
  CARD_CHARGE_STATUS_COLORS,
  CARD_CHARGE_MIN_AMOUNT,
  CARD_CHARGE_MAX_AMOUNT,
  CARD_CHARGE_REASON_MAX_LENGTH,
} from "../../constants/cardBinding";

const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;

const validateAmount = (_, value) => {
  if (!value) return Promise.resolve();
  const amount = Number(value);
  if (!AMOUNT_PATTERN.test(value) || amount < CARD_CHARGE_MIN_AMOUNT || amount > CARD_CHARGE_MAX_AMOUNT) {
    return Promise.reject(
      new Error(`Amount must be ${CARD_CHARGE_MIN_AMOUNT} to ${CARD_CHARGE_MAX_AMOUNT}, at most 2 decimals`)
    );
  }
  return Promise.resolve();
};

const ChargeSummary = ({ charge }) => (
  <Descriptions size="small" column={2} className="mt-2">
    <Descriptions.Item label="Charge Id">{charge.id}</Descriptions.Item>
    <Descriptions.Item label="Status">
      <Tag color={CARD_CHARGE_STATUS_COLORS[charge.status]}>{charge.status}</Tag>
    </Descriptions.Item>
    <Descriptions.Item label="Amount">{formatAmount(charge.amount, "RM")}</Descriptions.Item>
    <Descriptions.Item label="Card">{formatCardSummary(charge)}</Descriptions.Item>
    <Descriptions.Item label="Reason">{charge.reason}</Descriptions.Item>
    <Descriptions.Item label="Charged By">{charge.chargedBy}</Descriptions.Item>
    <Descriptions.Item label="Created At">{formatDateTime(charge.createdAt)}</Descriptions.Item>
    {charge.failureReason && (
      <Descriptions.Item label="Failure Reason">{charge.failureReason}</Descriptions.Item>
    )}
  </Descriptions>
);

// A 201 can already be FAILED when Paydibs rejects the call, so the outcome is always shown, not assumed
const chargeResultAlert = (charge) => {
  if (charge.status === "FAILED") {
    return { type: "error", message: "Charge failed", charge };
  }
  if (charge.status === "SUCCESS") {
    return { type: "success", message: "Charge succeeded", charge };
  }
  return {
    type: "info",
    message: "Charge submitted, waiting for Paydibs to settle it. Refresh the charge history for the result.",
    charge,
  };
};

const UserCardCharge = ({ merchant, onCharged }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const binding = merchant?.cardBinding;

  const submitCharge = async ({ amount, reason }) => {
    setSubmitting(true);
    setResult(null);
    try {
      const res = await axios.post("/api/user/charge", {
        merchantId: merchant.merchantId,
        amount,
        reason,
      });
      setResult(chargeResultAlert(res.data));
      form.resetFields();
      onCharged();
    } catch (error) {
      const blockingCharge = error.response?.status === 409 && error.response.data?.meta?.charge;
      if (blockingCharge) {
        setResult({ type: "warning", message: error.response.data.message, charge: blockingCharge });
      } else {
        message.error(error.response?.data?.message || error.message);
        // A timeout or 500 can follow an accepted charge, so reload the history to show it
        onCharged();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const confirmCharge = (values) => {
    Modal.confirm({
      title: "Charge bound card?",
      content: `${formatAmount(values.amount, "RM")} will be charged to ${formatCardSummary(binding)}.`,
      okText: "Charge",
      onOk: () => submitCharge(values),
    });
  };

  return (
    <Card size="small" title="Charge Card" className="mb-2">
      {!binding && (
        <Alert
          type="warning"
          showIcon
          className="mb-2"
          message="Merchant has no bound Paydibs card. A legacy SenangPay card cannot be charged."
        />
      )}
      <Form form={form} layout="inline" onFinish={confirmCharge}>
        <Form.Item
          name="amount"
          rules={[{ required: true, message: "Please input the amount!" }, { validator: validateAmount }]}
        >
          <Input prefix="RM" placeholder="Amount" style={{ width: 160 }} />
        </Form.Item>
        <Form.Item
          name="reason"
          rules={[{ required: true, message: "Please input the reason!" }]}
        >
          <Input placeholder="Reason" style={{ width: 360 }} maxLength={CARD_CHARGE_REASON_MAX_LENGTH} />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting} disabled={!binding || submitting}>
            Charge
          </Button>
        </Form.Item>
      </Form>
      {result && (
        <Alert
          type={result.type}
          showIcon
          closable
          className="mt-2"
          message={result.message}
          description={<ChargeSummary charge={result.charge} />}
          onClose={() => setResult(null)}
        />
      )}
    </Card>
  );
};

export default UserCardCharge;
